import { Component, FormEvent, ReactElement, } from "react";
import { PlusIcon, MinusIcon, TimesIcon, } from "./Icons";
import { classNames, Button, ButtonColor, } from ".";

import "./style.scss";

interface Props {
    value: number | undefined;
    name?: string;
    defaultValue: number;
    min?: number;
    max?: number;
    className?: string | string[];

    readonly?: boolean;
    disabled?: boolean;
    focused?: boolean;

    placeholder?: string;
    showChangeButtons?: boolean;
    showClearButton?: boolean;
    selectOnFocus?: boolean;
    useArrowKeys?: boolean; // up & down arrows
    renderInputGroup?: boolean; // render in input-group div?

    allowFloat: boolean;
    allowNegative: boolean;
    // TODO: support allowBlank
    allowBlank: boolean;

    // methods
    onChange?(value: number | undefined): void;
    onKeyPress?(e: React.KeyboardEvent<HTMLInputElement>): void;
}

interface State {
    value: number | undefined;
}

interface ValueObject {
    num: number | undefined;
    str: string;
}

const DEFAULT_VALUE = 0;

export class NumberInput extends Component<Props, State> {
    public static defaultProps = {
        defaultValue: DEFAULT_VALUE,

        readonly: false,
        disabled: false,
        focused: false,

        allowFloat: false,
        allowNegative: false,
        allowBlank: false,

        useArrowKeys: true,
        selectOnFocus: true,
        renderInputGroup: true,
    };

    private _allowNegative: boolean;

    constructor(props: Props) {
        super(props);

        const { value, min, allowNegative, } = this.props;
        const minValue = min ?? 0;
        this._allowNegative = allowNegative || (value !== undefined && value < 0) || (minValue < 0);

        this.state = {
            value: this._getInitialValue(),
        };

        this._handleChange = this._handleChange.bind(this);
        this._changeValue = this._changeValue.bind(this);
    }

    public render(): ReactElement {
        const { value, } = this.state;

        const {
            name,
            defaultValue,
            className,
            readonly,
            disabled,
            placeholder,
            focused,
            showChangeButtons,
            showClearButton,
            useArrowKeys,
            renderInputGroup,
            selectOnFocus,
            onKeyPress,
            allowBlank,
        } = this.props;

        const containerCss = classNames([
            renderInputGroup ? "input-group" : "",
            "__numberinput__",
        ]);

        const inputCss = classNames([
            "form-control",
            className ? (Array.isArray(className) ? [...className].join(" ") : className) : null,
            readonly ? "readonly" : null,
            disabled ? "disabled" : null,
        ]);

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value;

            if (value === "-" || (value === "" && !allowBlank)) {
                e.currentTarget.value = defaultValue.toString();
                this._onChange(defaultValue);
            }
        };

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            selectOnFocus && e.currentTarget.select();
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (useArrowKeys) {
                const key = e.key;

                if (key === "ArrowUp") {
                    e.preventDefault();
                    this._changeValue(1);
                    return;
                }

                if (key === "ArrowDown") {
                    e.preventDefault();
                    this._changeValue(-1);
                    return;
                }
            }
        };

        return (
            <div className={containerCss}>
                {showChangeButtons && this._renderChangeButtons()}

                <input
                    type="text"
                    value={value}
                    name={name}
                    className={inputCss}
                    readOnly={readonly}
                    disabled={disabled}
                    autoFocus={focused}
                    placeholder={placeholder}
                    onChange={this._handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    onKeyPress={onKeyPress}
                />

                {showClearButton && this._renderClearButton()}
            </div>
        );
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        const { value, } = this.props;
        if (prevProps.value !== value) {
            this.setState({
                value,
            });
        }
    }

    private _getInitialValue(): number | undefined {
        const {
            value,
            defaultValue,
            min,
            max,
            allowBlank,
        } = this.props;

        // handle allowBlank
        if (value === undefined && allowBlank) {
            return value;
        }

        // undefined isn't allowed, determine a proper value
        let n = value ? value : defaultValue;
        if (min !== undefined && n < min) {
            n = min;
        } else if (max !== undefined && n > max) {
            n = max;
        }

        return n;
    }

    private _handleChange(e: FormEvent<HTMLInputElement>): void {
        // if negative input is allowed the user may be in the process
        // of entering a negative so there's nothing to do right now
        if (this._allowNegative) {
            if (e.currentTarget.value === "-") {
                return;
            }
        }

        let value = this._getValueObject(e.currentTarget.value);

        e.currentTarget.value = value.str;
        // onChange && onChange(value.num);
        this._onChange(value.num);
    }

    private _getValueObject(value: string): ValueObject {
        const {
            allowFloat,
            defaultValue,
            min,
            max,
        } = this.props;

        // if the textbox is empty no need to continue
        // a blur event will handle the rest, if necessary
        if (value === "") {
            return {
                num: undefined,
                str: "",
            };
        }

        // get a number representation of the string
        let n = allowFloat ? parseFloat(value) : parseInt(value, 10);

        if (isNaN(n)) {
            // can't figure out what n is supposed to be
            // so we'll set it as the default
            n = defaultValue;
        }

        // check if the value is within the acceptible range
        if (min !== undefined && n < min) {
            n = min;
        } else if (max !== undefined && n > max) {
            n = max;
        }

        // lastly, check if negatives are allowed
        if (!this._allowNegative && n < 0) {
            n = DEFAULT_VALUE;
        }

        return {
            num: n,
            str: n.toString(),
        };
    }

    private _onChange(value: number | undefined): void {
        const { onChange, allowBlank, } = this.props;
        let fireOnChange = true;

        // if the value is undefined but the control doesn't
        // accept undefined, we shouldn't fire the onchange event
        if (value === undefined && !allowBlank) {
            fireOnChange = false;
        }

        this.setState({
            value,
        }, () => {
            fireOnChange && onChange && onChange(value);
        });
    }

    private _changeValue(d: number | undefined): void {
        const { value, } = this.state;
        const { min, max, allowBlank, } = this.props;

        // handle allowBlank
        if (d === undefined && allowBlank) {
            this._onChange(d);
            return;
        }

        // undefined isn't allowed, so determine the proper value
        // we need to explicitly cast some of these vars as number
        const n = value === undefined ? d : value + (d as number);

        // are we out of bounds?
        if ((min !== undefined) && ((n as number) < min)) {
            return;
        }

        if (max !== undefined && ((n as number) > max)) {
            return;
        }

        this._onChange(n);
    }

    private _renderChangeButtons(): ReactElement {
        const css = classNames([
            "btn-group",
            "btn-input-group",
        ]);

        return (
            <div className={css}>
                {this._renderChangeButton(1)}
                {this._renderChangeButton(-1)}
            </div>
        );
    }

    private _renderChangeButton(d: number): ReactElement {
        const { disabled, } = this.props;

        const icon = d === 1 ? PlusIcon : MinusIcon;
        const label = d === 1 ? "Increment" : "Decrement";
        const css = "btn-change";
        return (
            <Button
                className={css}
                label={label}
                onClick={() => this._changeValue(d)}
                disabled={disabled}
            >
                <span>{icon}</span>
            </Button>
        );
    }

    private _renderClearButton(): ReactElement | null {
        const { defaultValue, allowBlank, disabled, } = this.props;

        const handleClick = () => {
            const value = allowBlank ? undefined : defaultValue;
            this._onChange(value);
        }

        return (
            <Button
                label={"Clear value"}
                color={ButtonColor.Secondary}
                onClick={handleClick}
                disabled={disabled}
            >
                {TimesIcon}
            </Button>
        );
    }

    public getValue(): number | undefined {
        return this.state.value;
    }
}
